/**
 * ADWA HERMI — Global JavaScript
 * Handles: menu open/close, search overlay, subscribe modal,
 * header scroll behavior, and shared utilities.
 */

'use strict';

/* ── Utility ──────────────────────────────────────────────── */
const adwa = {
  body: document.body,

  lockScroll() {
    this.body.style.overflow = 'hidden';
    this.body.style.paddingRight = `${window.innerWidth - this.body.clientWidth}px`;
  },

  unlockScroll() {
    this.body.style.overflow = '';
    this.body.style.paddingRight = '';
  },

  trapFocus(el) {
    const focusable = el.querySelectorAll(
      'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    el.addEventListener('keydown', (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    });

    first?.focus();
  },
};

/* ── Menu Controller ──────────────────────────────────────── */
class AdwaMenuController {
  constructor() {
    this.menu = document.getElementById('AdwaMenu');
    this.overlay = document.getElementById('AdwaMenuOverlay');
    this.openBtns = document.querySelectorAll('[data-action="open-menu"]');
    this.closeBtns = document.querySelectorAll('[data-action="close-menu"]');
    this.dropdownTriggers = document.querySelectorAll('.adwa-menu__trigger');

    if (!this.menu) return;
    this._bindEvents();
  }

  open() {
    this.menu.classList.add('is-open');
    this.menu.setAttribute('aria-hidden', 'false');
    this.overlay.classList.add('is-active');
    this.overlay.setAttribute('aria-hidden', 'false');
    adwa.lockScroll();
    adwa.trapFocus(this.menu);

    document.querySelectorAll('[data-action="open-menu"]').forEach(btn => {
      btn.setAttribute('aria-expanded', 'true');
    });
  }

  close() {
    this.menu.classList.remove('is-open');
    this.menu.setAttribute('aria-hidden', 'true');
    this.overlay.classList.remove('is-active');
    this.overlay.setAttribute('aria-hidden', 'true');
    adwa.unlockScroll();

    document.querySelectorAll('[data-action="open-menu"]').forEach(btn => {
      btn.setAttribute('aria-expanded', 'false');
    });
  }

  _bindEvents() {
    this.openBtns.forEach(btn => btn.addEventListener('click', () => this.open()));
    this.closeBtns.forEach(btn => btn.addEventListener('click', () => this.close()));
    this.overlay?.addEventListener('click', () => this.close());

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.menu.classList.contains('is-open')) {
        this.close();
      }
    });

    // Dropdown toggles
    this.dropdownTriggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const item = trigger.closest('.adwa-menu__item');
        const isOpen = item.classList.contains('is-open');

        // Close all
        document.querySelectorAll('.adwa-menu__item.is-open').forEach(el => {
          el.classList.remove('is-open');
          el.querySelector('.adwa-menu__trigger')?.setAttribute('aria-expanded', 'false');
        });

        if (!isOpen) {
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }
}

/* ── Search Controller ────────────────────────────────────── */
class AdwaSearchController {
  constructor() {
    this.searchPanel = document.getElementById('AdwaSearch');
    this.openBtns = document.querySelectorAll('[data-action="open-search"]');
    this.closeBtns = document.querySelectorAll('[data-action="close-search"]');
    this.input = document.getElementById('AdwaSearchInput');
    this.comingSoon = document.getElementById('AdwaComingSoon');

    if (!this.searchPanel) return;
    this._bindEvents();
  }

  open() {
    this.searchPanel.classList.add('is-open');
    this.searchPanel.setAttribute('aria-hidden', 'false');
    adwa.lockScroll();
    setTimeout(() => this.input?.focus(), 100);
  }

  close() {
    this.searchPanel.classList.remove('is-open');
    this.searchPanel.setAttribute('aria-hidden', 'true');
    adwa.unlockScroll();
  }

  _bindEvents() {
    this.openBtns.forEach(btn => btn.addEventListener('click', () => this.open()));
    this.closeBtns.forEach(btn => btn.addEventListener('click', () => this.close()));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.searchPanel.classList.contains('is-open')) {
        this.close();
      }
    });

    // "Coming soon" buttons in search
    document.querySelectorAll('[data-coming-soon]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (this.comingSoon) {
          this.comingSoon.classList.toggle('is-visible');
          setTimeout(() => this.comingSoon.classList.remove('is-visible'), 3000);
        }
      });
    });

    // Cookie settings link in footer
    document.querySelector('.adwa-footer__cookie-settings-btn')?.addEventListener('click', () => {
      window.adwaCookieController?.openModal();
    });
  }
}

/* ── Video sound toggle ───────────────────────────────────
   Any element with [data-video-target="ID"] toggles the
   muted state of <video id="ID">. Works for hero + campaign
   video. Updates aria + swaps the muted/on icons.            */
function initAdwaSoundToggles() {
  document.querySelectorAll('.adwa-video-sound-toggle').forEach((btn) => {
    const video = document.getElementById(btn.dataset.videoTarget);
    if (!video) return;
    const mutedIcon = btn.querySelector('.adwa-video-sound-toggle__muted');
    const onIcon = btn.querySelector('.adwa-video-sound-toggle__on');

    btn.addEventListener('click', () => {
      const willUnmute = video.muted;
      video.muted = !willUnmute;
      // If we unmuted but autoplay was muted, ensure playback continues
      if (willUnmute) video.play().catch(() => {});
      btn.setAttribute('aria-pressed', willUnmute ? 'true' : 'false');
      btn.setAttribute('aria-label', willUnmute ? 'Mute video' : 'Unmute video');
      if (mutedIcon) mutedIcon.style.display = willUnmute ? 'none' : '';
      if (onIcon) onIcon.style.display = willUnmute ? '' : 'none';
    });
  });
}

/* ── Init on DOM ready ────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  window.adwaMenu = new AdwaMenuController();
  window.adwaSearch = new AdwaSearchController();
  initAdwaSoundToggles();
  // Gallery script is loaded via Liquid in PDP section when needed.
});
